import React from "react";
import { Link } from "react-router-dom";
import { Activity, ShieldCheck, Zap, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";

export default function IndustryPage({ name, desc, icon: IconSymbol, pageKey }) {
  const caseStudies = [
    { title: "Global Auto Parts Corp SLA Recovery", impact: "Increased on-time delivery from 74% to 98.4% in 60 days.", result: "Saved $1.4M in logistical delays" },
    { title: "Securing Medical Devices KYC/AML Compliance", impact: "Automated real-time insurance tracking for 2,400 global suppliers.", result: "Reduced manual audit overhead by 85%" }
  ];

  return (
    <div className="bg-[#05050A] text-white min-h-screen py-24 relative overflow-hidden" data-testid={`industry-page-${pageKey}`}>
      
      {/* Background visual glow */}
      <div className="absolute top-40 left-1/4 w-[500px] h-[500px] bg-[#EA7C3D]/5 rounded-full blur-[140px] pointer-events-none" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header Tag */}
        <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-[#EA7C3D] tracking-wide mb-6">
          <Sparkles className="w-3 h-3 text-[#7C3AED]" />
          <span>Sectors &gt; {name} Solutions</span>
        </div>

        {/* Hero Section */}
        <div className="space-y-6 mb-16">
          <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tighter leading-none">{name}</h1>
          <p className="text-lg text-[#ABA3C2] font-body max-w-3xl leading-relaxed">{desc}</p>
        </div>

        {/* Beautiful Sector Card */}
        <div className="p-8 rounded-3xl bg-[#14101F]/80 border border-white/10 backdrop-blur-xl relative overflow-hidden mb-16">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#EA7C3D]/10 to-transparent pointer-events-none rounded-bl-full" />
          
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-white font-display">Targeted Supply-Chain Defense & SLA Compliance</h3>
            <p className="text-sm text-[#ABA3C2] leading-relaxed">
              We understand that the {name} sector demands absolute consistency, real-time alert triggers, and deep integration with your current accounting resources. Manual audits are too slow to stop expensive supply-chain failures.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4">
              <div className="space-y-2">
                <div className="text-xs font-bold text-[#6E6785] uppercase tracking-wider">Primary Need</div>
                <div className="text-sm font-semibold text-white">Continuous Compliance</div>
              </div>
              <div className="space-y-2">
                <div className="text-xs font-bold text-[#6E6785] uppercase tracking-wider">Aroveniq Feature</div>
                <div className="text-sm font-semibold text-white">Real-Time Risk Alerts</div>
              </div>
              <div className="space-y-2">
                <div className="text-xs font-bold text-[#6E6785] uppercase tracking-wider">Key Integration</div>
                <div className="text-sm font-semibold text-white">ERP Webhooks (Coupa, SAP)</div>
              </div>
            </div>
          </div>
        </div>

        {/* Case Studies Sector Showcase */}
        <div className="space-y-8">
          <h2 className="text-2xl font-bold font-display tracking-tight border-b border-white/10 pb-4">Relevant Case Studies for {name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((study, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/5 border border-white/5 flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <h3 className="font-bold text-white text-base font-display">{study.title}</h3>
                  <p className="text-xs text-[#ABA3C2] font-body leading-relaxed">{study.impact}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-xs font-semibold text-green-400 font-mono">
                  Result: {study.result}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <Link
            to="/contact"
            className="inline-flex px-8 py-3.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EA7C3D] text-white font-semibold text-sm hover:shadow-[0_0_20px_rgba(124,58,237,0.4)] transition-all items-center group"
          >
            <span>Discuss your {name} Requirements</span>
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

      </div>
    </div>
  );
}